import React, {useLayoutEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import {toast} from 'react-toastify';
import {PromptLibraryApi} from '../../../../api/promptLibraryApi';
import {promptAskPhrase} from '../../../../utils/promptAskPhrase';
import {Switch} from '../../../buttons/Swtich';
import {FaIcon} from '../../../FaIcon';
import {MOMENTOS, ROLES} from '../promptTaxonomy';
import {isAck, isPromptPayload} from '../libraryState';
import {PlaceholderNotes} from './PlaceholderNotes';
import {findSimilarTitle} from './similarTitles';

const RAW = {interpolation: {escapeValue: false}};

// El `_title` del modelo `Prompt` es `max_length=200`: pasarse da un 400 que ningún reintento arregla.
export const TITLE_MAX_LENGTH = 200;

export const EDITOR_NEW = 'new';
export const EDITOR_EDIT = 'edit';
export const EDITOR_DUPLICATE = 'duplicate';

const TITLE_KEYS = {
    [EDITOR_NEW]: 'prompt_editor.title_new',
    [EDITOR_EDIT]: 'prompt_editor.title_edit',
    [EDITOR_DUPLICATE]: 'prompt_editor.title_duplicate',
};

// El campo del formulario que marca cada error, en el orden en que aparecen en pantalla.
const FIELD_ORDER = ['title', 'client', 'text'];
const ERROR_FIELD_OF = {title: 'title', text: 'text', isAllClients: 'client', clientId: 'client'};

/**
 * Los errores del formulario, por campo: cada uno se muestra debajo del suyo. El alcance sólo se
 * valida al crear (al editar no se cambia).
 */
export function validateForm(form, isEdit) {
    const errors = {};
    if (!form.title.trim()) errors.title = 'prompt_editor.validation_title';
    if (!isEdit && !form.isAllClients && !form.clientId) errors.client = 'prompt_editor.validation_client';
    if (!form.text.trim()) errors.text = 'prompt_editor.validation_text';
    return errors;
}

/**
 * El nombre de la copia: el formato (`{{title}} (copia)`) lo da i18n, y el nombre base se recorta
 * para que el resultado no pase el tope del modelo.
 */
export function copyTitleFor(title, format) {
    const suffixLength = format('').length;
    return format(String(title || '').slice(0, Math.max(0, TITLE_MAX_LENGTH - suffixLength)));
}

/**
 * El estado inicial del formulario según el modo. Se calcula una vez: la página monta el modal al
 * abrirlo y lo keyea por modo y prompt, así que no hace falta re-hidratarlo (y un efecto que lo
 * hiciera pisaría lo escrito si la lista se recarga con el modal abierto).
 *
 * «Duplicar» existe para cambiar el alcance («lo armé para Pepito y ahora sirve para todos»), así
 * que abre con el alcance CONTRARIO: el de un cliente pasa a todos, y el de todos pide elegir uno.
 * `formatCopyTitle` arma el nombre de la copia (i18n).
 */
export function initialForm(mode, prompt, defaultClientId = null, formatCopyTitle = (title) => title) {
    if (mode === EDITOR_NEW || !prompt) {
        return {
            title: '', text: '', preview: '', role: '', momentos: [], portal: false,
            isAllClients: !defaultClientId, clientId: defaultClientId ? String(defaultClientId) : '',
        };
    }
    const base = {
        text: prompt.prompt_text || '',
        preview: prompt.value_preview || '',
        role: prompt.role || '',
        momentos: [...(prompt.momentos || [])],
        portal: false,
    };
    if (mode === EDITOR_DUPLICATE) {
        return {
            ...base,
            title: copyTitleFor(prompt.title, formatCopyTitle),
            isAllClients: Boolean(prompt.client),
            clientId: '',
        };
    }
    return {
        ...base,
        title: prompt.title || '',
        isAllClients: !prompt.client,
        clientId: prompt.client ? String(prompt.client.id) : '',
    };
}

/**
 * Nuevo, editar y duplicar un prompt de la agencia (mockup 3). Tres campos obligatorios —nombre,
 * para qué clientes y el texto— y a la derecha lo que va a ver la IA y la frase con la que se pide.
 * Momento, rol y portal van plegados; nivel e ingredientes no se piden (los no oficiales nunca se
 * bloquean por ingredientes, contrato §4.2).
 *
 * El alcance no se cambia al editar: el `PUT` del Backend no lo acepta. Para eso está «Duplicar».
 */
export function PromptEditorModal({
    mode, prompt, agencyName, clients, ownPrompts, defaultClientId = null, onClose, onSaved,
}) {
    const {t} = useTranslation();
    const [form, setForm] = useState(() => initialForm(mode, prompt, defaultClientId,
        (title) => t('prompt_editor.copy_title', {title, ...RAW})));
    const [moreOpen, setMoreOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const textRef = useRef(null);
    const nameRef = useRef(null);
    const clientRef = useRef(null);
    const isEdit = mode === EDITOR_EDIT;

    // Corregir un campo borra SU error, no los de los demás.
    const setField = (field, value) => {
        const errorField = ERROR_FIELD_OF[field];
        if (errorField) {
            setErrors((previous) => {
                if (!previous[errorField]) return previous;
                const next = {...previous};
                delete next[errorField];
                return next;
            });
        }
        setForm((previous) => ({...previous, [field]: value}));
    };

    // El textarea crece con el texto: un prompt pegado de 40 líneas en una caja de seis obliga a
    // scrollear adentro del modal para revisarlo.
    useLayoutEffect(() => {
        const area = textRef.current;
        if (!area) return;
        area.style.height = 'auto';
        area.style.height = `${area.scrollHeight + 2}px`;
    }, [form.text]);

    const client = (clients || []).find((item) => String(item.id) === String(form.clientId)) || null;
    const clientName = client ? client.name : '';
    // El cliente con el que se va a pedir (la vista previa del pedido): el elegido, o el del prompt
    // que se edita si la lista no lo trae.
    const askClient = form.isAllClients ? null : (client || (prompt && prompt.client) || null);
    // El selector ofrece sólo los activos: `create/` resuelve el cliente con los activos y a uno
    // inactivo lo rechaza con 404, aunque el payload de la pantalla lo liste (para el filtro y para
    // nombrar los prompts viejos).
    const selectableClients = (clients || []).filter((item) => item.is_active !== false);
    const similar = findSimilarTitle(form.title, {
        isAllClients: form.isAllClients,
        clientId: form.clientId,
        excludeId: isEdit && prompt ? prompt.id : null,
    }, ownPrompts);

    const handleClose = () => {
        if (!saving) onClose();
    };

    const toggleMomento = (momento) => {
        setField('momentos', form.momentos.includes(momento)
            ? form.momentos.filter((value) => value !== momento)
            : [...form.momentos, momento]);
    };

    const finish = (data, portalVisible) => {
        setSaving(false);
        toast.success(t(isEdit ? 'prompt_editor.updated_toast' : 'prompt_editor.created_toast'));
        onSaved({
            mode,
            data,
            clientId: form.isAllClients ? null : Number(form.clientId),
            portalVisible,
        });
    };

    const fail = () => {
        setSaving(false);
        toast.error(t('prompt_editor.error_toast'));
    };

    // El alta no acepta `portal_visible`: se publica con un segundo request. Si ése falla, el prompt
    // YA existe — se cierra igual y se avisa que se puede prender desde el menú de la fila.
    const publishThenFinish = (data) => {
        PromptLibraryApi.setPortalVisibility(data.id, true)
            .then((response) => {
                if (isAck(response)) {
                    finish(data, true);
                    return;
                }
                toast.warning(t('prompt_editor.portal_error_toast'));
                finish(data, false);
            })
            .catch(() => {
                toast.warning(t('prompt_editor.portal_error_toast'));
                finish(data, false);
            });
    };

    const handleSave = () => {
        if (saving) return;
        const invalid = validateForm(form, isEdit);
        const firstInvalid = FIELD_ORDER.find((field) => invalid[field]);
        if (firstInvalid) {
            setErrors(invalid);
            const ref = {title: nameRef, client: clientRef, text: textRef}[firstInvalid];
            if (ref.current) ref.current.focus();
            return;
        }
        setSaving(true);
        const payload = {
            title: form.title.trim(),
            prompt_text: form.text.trim(),
            value_preview: form.preview.trim(),
            role: form.role,
            momentos: form.momentos,
        };
        const request = isEdit
            ? PromptLibraryApi.update(prompt.id, payload)
            : PromptLibraryApi.create({
                ...payload,
                scope: form.isAllClients ? 'AGENCY' : 'CLIENT',
                ...(form.isAllClients ? {} : {agency_client_id: Number(form.clientId)}),
            });
        request.then((data) => {
            if (!isPromptPayload(data)) {
                fail();
                return;
            }
            if (!isEdit && form.portal) {
                publishThenFinish(data);
                return;
            }
            finish(data, isEdit ? Boolean(data.portal_visible) : false);
        }).catch(fail);
    };

    const invalidProps = (field, errorId) => (errors[field]
        ? {'aria-invalid': true, 'aria-describedby': errorId}
        : {});

    const renderFieldError = (field, errorId) => errors[field] && (
        <p id={errorId} className="agp-editor__field-error">{t(errors[field])}</p>
    );

    const scopeText = form.isAllClients
        ? t('prompt_editor.scope_all')
        : `${t('prompt_editor.scope_one')}: ${clientName || (prompt && prompt.client ? prompt.client.name : '')}`;

    const renderScope = () => {
        if (isEdit) {
            return (
                <div className="agp-editor__field">
                    <span className="agp-editor__label">{t('prompt_editor.scope_label')}</span>
                    <p className="agp-editor__fixed">{scopeText}</p>
                    <p className="agp-editor__hint">{t('prompt_editor.scope_locked_hint')}</p>
                </div>
            );
        }
        return (
            <div className="agp-editor__field">
                <span className="agp-editor__label" id="agp-editor-scope">{t('prompt_editor.scope_label')}</span>
                <div className="agp-editor__scope">
                    <div className="agp-seg" role="group" aria-labelledby="agp-editor-scope">
                        <button type="button" className={`agp-seg__btn${form.isAllClients ? ' -on' : ''}`}
                                aria-pressed={form.isAllClients} onClick={() => setField('isAllClients', true)}>
                            {form.isAllClients && <FaIcon iconName="check"/>}
                            {t('prompt_editor.scope_all')}
                        </button>
                        <button type="button" className={`agp-seg__btn${form.isAllClients ? '' : ' -on'}`}
                                aria-pressed={!form.isAllClients} onClick={() => setField('isAllClients', false)}>
                            {!form.isAllClients && <FaIcon iconName="check"/>}
                            {t('prompt_editor.scope_one')}
                        </button>
                    </div>
                    {!form.isAllClients && (
                        <select ref={clientRef} className="agp-select" value={form.clientId}
                                aria-label={t('prompt_editor.scope_pick_client')}
                                {...invalidProps('client', 'agp-editor-client-error')}
                                onChange={(event) => setField('clientId', event.target.value)}>
                            <option value="">{t('prompt_editor.scope_pick_client')}</option>
                            {selectableClients.map((item) => (
                                <option key={item.id} value={String(item.id)}>{item.name}</option>
                            ))}
                        </select>
                    )}
                </div>
                {renderFieldError('client', 'agp-editor-client-error')}
            </div>
        );
    };

    const renderMoreOptions = () => (
        <div className="agp-editor__more">
            <div className="agp-editor__field">
                <span className="agp-editor__label" id="agp-editor-moment">{t('prompt_editor.moment_label')}</span>
                <div className="agp-editor__chips" role="group" aria-labelledby="agp-editor-moment">
                    {MOMENTOS.map((momento) => {
                        const on = form.momentos.includes(momento);
                        return (
                            <button key={momento} type="button" className={`agp-pill${on ? ' -on' : ''}`}
                                    aria-pressed={on} onClick={() => toggleMomento(momento)}>
                                {on && <FaIcon iconName="check"/>}
                                {t(`prompt_library.momentos.${momento}`)}
                            </button>
                        );
                    })}
                </div>
            </div>
            <div className="agp-editor__field">
                <label className="agp-editor__label" htmlFor="agp-editor-role">{t('prompt_editor.role_label')}</label>
                <select id="agp-editor-role" className="agp-select" value={form.role}
                        onChange={(event) => setField('role', event.target.value)}>
                    <option value="">{t('prompt_editor.role_none')}</option>
                    {ROLES.map((role) => <option key={role} value={role}>{t(`prompt_library.roles.${role}`)}</option>)}
                </select>
            </div>
            {!isEdit && (
                <div className="agp-editor__switch">
                    <div>
                        <span className="agp-editor__label" id="agp-editor-portal">{t('prompt_editor.portal_label')}</span>
                        <p className="agp-editor__hint">{t('prompt_editor.portal_hint')}</p>
                    </div>
                    <Switch name="agp-editor-portal" checked={form.portal} style={{margin: 0}}
                            aria-labelledby="agp-editor-portal"
                            onChange={(event) => setField('portal', event.target.checked)}/>
                </div>
            )}
        </div>
    );

    const sourceLabel = form.isAllClients
        ? t('prompt_editor.right_source_all', {agency: agencyName, ...RAW})
        : t('prompt_editor.right_source_one', {agency: agencyName, client: clientName || '…', ...RAW});

    return (
        // El foco va al nombre al MONTAR el input (`autoFocus`), no al terminar la animación: lo que se
        // tipeaba antes se perdía, y con la pestaña en segundo plano el timer de la animación tarda
        // segundos. `autoFocus={false}` en el Modal para que reactstrap no se lo saque al diálogo.
        // El fondo es estático para no perder lo escrito con un clic afuera, y `scrollable` deja el
        // pie siempre a la vista.
        <Modal isOpen toggle={handleClose} className="agp-editor" backdrop="static" autoFocus={false} scrollable>
            <ModalHeader toggle={handleClose} closeAriaLabel={t('agency_prompts.close')}>
                {t(TITLE_KEYS[mode])}
            </ModalHeader>
            <ModalBody>
                <div className="agp-editor__grid">
                    <div className="agp-editor__form">
                        <div className="agp-editor__field">
                            <label className="agp-editor__label" htmlFor="agp-editor-name">{t('prompt_editor.name_label')}</label>
                            <input id="agp-editor-name" ref={nameRef} className="agp-input" value={form.title} autoFocus
                                   maxLength={TITLE_MAX_LENGTH}
                                   {...invalidProps('title', 'agp-editor-name-error')}
                                   onChange={(event) => setField('title', event.target.value)}
                                   placeholder={t('prompt_editor.name_placeholder')}/>
                            {renderFieldError('title', 'agp-editor-name-error')}
                            <p className="agp-editor__hint">{t('prompt_editor.name_hint')}</p>
                        </div>

                        {renderScope()}

                        <div className="agp-editor__field">
                            <label className="agp-editor__label" htmlFor="agp-editor-text">
                                {t('prompt_editor.text_label')}
                                <i> · {t('prompt_editor.text_label_hint')}</i>
                            </label>
                            <textarea id="agp-editor-text" ref={textRef} className="agp-input agp-editor__text" rows={6}
                                      value={form.text} onChange={(event) => setField('text', event.target.value)}
                                      {...invalidProps('text', 'agp-editor-text-error')}
                                      placeholder={t('prompt_editor.text_placeholder')}/>
                            {renderFieldError('text', 'agp-editor-text-error')}
                            <PlaceholderNotes text={form.text} isAllClients={form.isAllClients} clientName={clientName}/>
                        </div>

                        <div className="agp-editor__field">
                            <label className="agp-editor__label" htmlFor="agp-editor-preview">
                                {t('prompt_editor.preview_label')}
                                <i> · {t('prompt_editor.preview_label_hint')}</i>
                            </label>
                            <input id="agp-editor-preview" className="agp-input" value={form.preview}
                                   onChange={(event) => setField('preview', event.target.value)}
                                   placeholder={t('prompt_editor.preview_placeholder')}/>
                            <p className="agp-editor__hint">{t('prompt_editor.preview_hint')}</p>
                        </div>

                        <button type="button" className="agp-editor__fold" aria-expanded={moreOpen}
                                onClick={() => setMoreOpen((value) => !value)}>
                            <FaIcon iconName={moreOpen ? 'chevron-down' : 'chevron-right'}/>
                            {t('prompt_editor.more_options')}
                        </button>
                        {moreOpen && renderMoreOptions()}
                    </div>

                    <aside className="agp-editor__side">
                        <h3 className="agp-editor__side-title">{t('prompt_editor.right_title')}</h3>
                        <div className="agp-editor__idx">
                            <span className="agp-editor__src">{sourceLabel}</span>
                            <strong className="agp-editor__idx-name">{form.title.trim() || '…'}</strong>
                            {form.preview.trim() && <span className="agp-editor__idx-preview">{form.preview.trim()}</span>}
                        </div>
                        <div className="agp-editor__say">
                            <small>{t('prompt_editor.right_ask_title')}</small>
                            {promptAskPhrase(form.title.trim() || '…', askClient)}
                        </div>
                        {similar && (
                            <p className="agp-editor__warn" role="status">
                                {t('prompt_editor.duplicate_name_warning', {title: similar.title, ...RAW})}
                            </p>
                        )}
                    </aside>
                </div>
            </ModalBody>
            {/* El par de la pantalla (`agp-btn`), no el del sistema: `btn -negative` va en mayúsculas y
                con otro alto que `btn -alt`, y el hover de éste deja el texto en 2,4:1. */}
            <ModalFooter>
                <span className="agp-editor__footnote">{t('prompt_editor.footer_team', {agency: agencyName, ...RAW})}</span>
                <button type="button" className="agp-btn" onClick={handleClose} disabled={saving}>
                    {t('prompt_editor.cancel')}
                </button>
                <button type="button" className="agp-btn -primary" onClick={handleSave} disabled={saving}>
                    {saving
                        ? t('prompt_editor.saving')
                        : t(isEdit ? 'prompt_editor.save_changes' : 'prompt_editor.save')}
                </button>
            </ModalFooter>
        </Modal>
    );
}
